import { makeAuthenticatedRequest } from './authService';

// ========================================
// Types for Critical Thinking Service
// ========================================

export interface ThinkingExercise {
  exerciseId: string;
  exerciseType: string;
  difficulty: string;
  topic: string | null;
  problemText: string;
  prompts: string[];
  exerciseData?: any;
  createdAt: string;
}

export interface ExerciseEvaluation {
  score: number;
  summary: string;
  strengths: string[];
  improvements: string[];
  detailedFeedback: string;
}

export interface ExerciseResponse {
  responseId: string;
  exerciseId: string;
  userResponse: string;
  evaluation: ExerciseEvaluation;
  createdAt: string;
}

export interface GenerateExerciseParams {
  exerciseType?: 'critical_thinking' | 'logical_reasoning' | 'argument_analysis' | 'problem_solving';
  difficulty?: 'easy' | 'medium' | 'hard';
  topic?: string;
}

export interface SubmitResponseParams {
  exerciseId: string;
  userResponse: string;
}

// ========================================
// Critical Thinking Service
// ========================================

export const criticalThinkingService = {
  /**
   * Generate a new thinking exercise
   */
  async generateExercise(params: GenerateExerciseParams = {}): Promise<ThinkingExercise> {
    try {
      const response = await makeAuthenticatedRequest('/thinking-exercises/generate', {
        method: 'POST',
        body: JSON.stringify({
          exerciseType: params.exerciseType || 'critical_thinking',
          difficulty: params.difficulty || 'medium',
          topic: params.topic || null
        })
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to generate exercise');
      }

      return response.exercise;
    } catch (error: any) {
      console.error('Error generating exercise:', error);
      throw error;
    }
  },

  /**
   * Get exercise details by ID
   */
  async getExercise(exerciseId: string): Promise<ThinkingExercise> {
    try {
      const response = await makeAuthenticatedRequest(`/thinking-exercises/exercises/${exerciseId}`);

      if (!response.success) {
        throw new Error(response.message || 'Failed to get exercise');
      }

      return response.exercise;
    } catch (error: any) {
      console.error('Error getting exercise:', error);
      throw error;
    }
  },

  /**
   * Submit user response and get AI evaluation
   */
  async submitResponse(params: SubmitResponseParams): Promise<ExerciseEvaluation> {
    try {
      const response = await makeAuthenticatedRequest('/thinking-exercises/submit', {
        method: 'POST',
        body: JSON.stringify({
          exerciseId: params.exerciseId,
          userResponse: params.userResponse
        })
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to submit response');
      }

      return response.evaluation;
    } catch (error: any) {
      console.error('Error submitting response:', error);
      throw error;
    }
  },

  /**
   * Get user's response history
   */
  async getResponseHistory(limit: number = 50, offset: number = 0): Promise<ExerciseResponse[]> {
    try {
      const response = await makeAuthenticatedRequest(
        `/thinking-exercises/history?limit=${limit}&offset=${offset}`
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to get response history');
      }

      return response.responses || [];
    } catch (error: any) {
      console.error('Error getting response history:', error);
      throw error;
    }
  },

  /**
   * Get user's response for a specific exercise
   */
  async getExerciseResponse(exerciseId: string): Promise<ExerciseResponse | null> {
    try {
      const response = await makeAuthenticatedRequest(`/thinking-exercises/${exerciseId}/response`);

      if (!response.success) {
        throw new Error(response.message || 'Failed to get exercise response');
      }

      return response.response || null;
    } catch (error: any) {
      console.error('Error getting exercise response:', error);
      throw error;
    }
  }
};

export default criticalThinkingService;

